import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import HeroEditor from '../components/HeroEditor';
import ServicesEditor from '../components/ServicesEditor';
import PortfolioEditor from '../components/PortfolioEditor';
import ContactEditor from '../components/ContactEditor';
import TestimonialsEditor from '../components/TestimonialsEditor';
import FooterEditor from '../components/FooterEditor';
import AboutEditor from '../components/AboutEditor';

interface SiteContent {
  hero: any;
  services: any[];
  portfolio: any[];
  contact: any;
  testimonials: any[];
  footer: any;
  about: any;
}

export default function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('hero');
  const [content, setContent] = useState<SiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadContent();
  }, [navigate]);

  const loadContent = async () => {
    try {
      const response = await fetch('/backend/api/get_site_content.php');
      const result = await response.json();
      
      if (result.success) {
        setContent(result.data);
      } else {
        showMessage('error', 'Error al cargar el contenido');
      }
    } catch (error) {
      showMessage('error', 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    if (!content) return;
    
    setSaving(true);
    try {
      const response = await fetch('/backend/api/save_site_content.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('success', '¡Cambios guardados exitosamente!');
      } else {
        showMessage('error', result.message || 'Error al guardar');
      }
    } catch (error) {
      showMessage('error', 'Error al conectar con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const updateSection = (section: keyof SiteContent, data: any) => {
    if (!content) return;
    setContent({
      ...content,
      [section]: data,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-5xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Cargando contenido...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-red-600"></i>
          <p className="mt-4 text-gray-600">Error al cargar el contenido</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection={activeSection} onSectionChange={setActiveSection}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Panel de Administración</h1>
          <p className="text-gray-600 mt-1">Gestiona todo el contenido de tu sitio web</p>
        </div>
        <button
          onClick={saveContent}
          disabled={saving}
          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Guardar Cambios
            </>
          )}
        </button>
      </div>

      {message && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <i className={message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          <span>{message.text}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {activeSection === 'hero' && (
          <HeroEditor data={content.hero} onChange={(data) => updateSection('hero', data)} />
        )}
        {activeSection === 'services' && (
          <ServicesEditor data={content.services} onChange={(data) => updateSection('services', data)} />
        )}
        {activeSection === 'portfolio' && (
          <PortfolioEditor data={content.portfolio} onChange={(data) => updateSection('portfolio', data)} />
        )}
        {activeSection === 'contact' && (
          <ContactEditor data={content.contact} onChange={(data) => updateSection('contact', data)} />
        )}
        {activeSection === 'testimonials' && (
          <TestimonialsEditor data={content.testimonials} onChange={(data) => updateSection('testimonials', data)} />
        )}
        {activeSection === 'footer' && (
          <FooterEditor data={content.footer} onChange={(data) => updateSection('footer', data)} />
        )}
        {activeSection === 'about' && (
          <AboutEditor data={content.about} onChange={(data) => updateSection('about', data)} />
        )}
      </div>
    </AdminLayout>
  );
}
